import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Typography,
  List,
  Flex,
  Popconfirm,
  message,
} from "antd";
import { BASE_API_URL } from "../../../../utils/consts";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { format, getMonth, getYear } from "date-fns";

const { Text } = Typography;
const { Option } = Select;

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  modalData?: any;
  userId: any;
  onUpdate: () => void;
  selectedMonth: any;
  mode?: "create" | "edit";
  userWorkLocations?: any[];
  defaultLocationId?: string;
};

const LoanPaymentModal: React.FC<Props> = ({
  isOpen,
  onCloseModal,
  modalData,
  userId,
  onUpdate,
  selectedMonth,
  userWorkLocations = [],
  defaultLocationId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [repaymentInputs, setRepaymentInputs] = useState<
    Record<
      string,
      {
        amount?: string;
        date?: any;
        description?: string;
      }
    >
  >({});
  const [repaymentLoading, setRepaymentLoading] = useState<
    Record<string, boolean>
  >({});

  const handleCancel = () => {
    form.resetFields();
    onCloseModal();
  };

  const onFinish = async (values: any) => {
    const baseLoan: any = {
      type: values.type,
      totalAmount: Number(values.totalAmount),
      startDate:
        typeof values.startDate?.toDate === "function"
          ? values.startDate.toDate()
          : values.startDate,
      notes: values.notes,
      didHeRepayTheDebt: false,
      locationId: values.locationId || null,
    };

    if (values.type === "installemnt_loan") {
      baseLoan.monthlyInstallment = Number(values.monthlyInstallment);
      baseLoan.numberOfIntallments = Number(values.numberOfIntallments);
    }

    const payload = { loan: baseLoan };

    try {
      // Ako nemamo paymentId ili dobijemo 404, probaj da prvo kreiraš record
      if (!modalData?._id) {
        await axios.post(`${BASE_API_URL}/userPayments/`, {
          userId,
          ...payload,
        });
      } else {
        try {
          await axios.put(
            `${BASE_API_URL}/userPayments/${modalData._id}/${userId}`,
            payload,
          );
        } catch (err: any) {
          if (err?.response?.status === 404) {
            await axios.post(`${BASE_API_URL}/userPayments/`, {
              userId,
              ...payload,
            });
          } else {
            throw err;
          }
        }
      }

      message.success("Pozajmica sačuvana!");
      onUpdate();
      onCloseModal();
      form.resetFields();
    } catch (error) {
      console.error(error);
      message.error("Greška prilikom čuvanja pozajmice.");
    }
  };

  const calculateFlexibleRepaymentsForMonth = (
    loan: any,
    selectedMonthValue: any,
  ) => {
    if (!loan?.flexibleRepayments?.length) return 0;

    const selectedDate =
      typeof selectedMonthValue?.toDate === "function"
        ? selectedMonthValue.toDate()
        : new Date(selectedMonthValue);
    const targetYear = getYear(selectedDate);
    const targetMonth = getMonth(selectedDate);

    return loan.flexibleRepayments.reduce((sum: number, repayment: any) => {
      if (!repayment?.date) return sum;
      const repaymentDate = new Date(repayment.date);
      if (
        getYear(repaymentDate) === targetYear &&
        getMonth(repaymentDate) === targetMonth
      ) {
        return sum + Number(repayment.amount || 0);
      }
      return sum;
    }, 0);
  };

  const calculateFlexibleOutstanding = (loan: any) => {
    if (loan?.didHeRepayTheDebt) return 0;
    const repaid = (loan?.flexibleRepayments || []).reduce(
      (acc: number, repayment: any) => acc + Number(repayment.amount || 0),
      0,
    );
    return Math.max(0, Number(loan.totalAmount || 0) - repaid);
  };

  const handleRepaymentFieldChange = (
    loanId: string,
    field: "amount" | "date" | "description",
    value: any,
  ) => {
    setRepaymentInputs((prev) => ({
      ...prev,
      [loanId]: {
        ...(prev[loanId] || {}),
        [field]: value,
      },
    }));
  };

  const submitFlexibleRepayment = async (loanId: string) => {
    const repayment = repaymentInputs[loanId] || {};
    if (!repayment.amount) {
      message.error("Unesite iznos otplate.");
      return;
    }

    if (!modalData?.userId?._id) {
      message.error("Nedostaje korisnik za otplatu.");
      return;
    }

    const repaymentDateInput = repayment.date ?? selectedMonth;
    const normalizedDate =
      typeof repaymentDateInput?.toDate === "function"
        ? repaymentDateInput.toDate()
        : repaymentDateInput;

    try {
      setRepaymentLoading((prev) => ({ ...prev, [loanId]: true }));
      await axios.post(
        `${BASE_API_URL}/userPayments/${modalData.userId._id}/loan/${loanId}/repay`,
        {
          amount: Number(repayment.amount),
          date: normalizedDate,
          description: repayment.description,
        },
      );
      message.success("Otplata je zabeležena.");
      setRepaymentInputs((prev) => ({
        ...prev,
        [loanId]: { amount: "", date: selectedMonth, description: "" },
      }));
      onUpdate();
    } catch (error) {
      console.error(error);
      message.error("Greška prilikom čuvanja uplate.");
    } finally {
      setRepaymentLoading((prev) => ({ ...prev, [loanId]: false }));
    }
  };

  /**
   * ✅ Filtriramo samo pozajmice koje su AKTIVNE u selektovanom mesecu
   */
  const filterLoansForSelectedMonth = (loans: any[] = []) => {
    const currentYear = getYear(new Date(selectedMonth));
    const currentMonth = getMonth(new Date(selectedMonth));

    return loans.filter((loan) => {
      const loanStart = new Date(loan.startDate);
      const loanStartYear = getYear(loanStart);
      const loanStartMonth = getMonth(loanStart);

      if (loan.type === "advance_salary") {
        return currentYear === loanStartYear && currentMonth === loanStartMonth;
      }

      if (loan.type === "installemnt_loan") {
        const monthsPassed =
          (currentYear - loanStartYear) * 12 + (currentMonth - loanStartMonth);
        return monthsPassed >= 0 && monthsPassed < loan.numberOfIntallments;
      }

      if (loan.type === "flexible_payout") {
        const outstanding =
          loan.outstandingAmount ?? calculateFlexibleOutstanding(loan);
        const repaidThisMonth = calculateFlexibleRepaymentsForMonth(
          loan,
          selectedMonth,
        );
        // Show if there's outstanding balance OR if there were repayments this month
        return outstanding > 0 || repaidThisMonth > 0;
      }

      return false;
    });
  };

  const activeLoans = filterLoansForSelectedMonth(modalData?.loan);
  const totalLoans =
    activeLoans?.reduce(
      (acc: number, l: any) =>
        acc +
        (l.type === "installemnt_loan"
          ? l.monthlyInstallment || 0
          : l.type === "flexible_payout"
            ? calculateFlexibleRepaymentsForMonth(l, selectedMonth)
            : l.totalAmount || 0),
      0,
    ) || 0;

  return (
    <Modal
      title={`Pozajmice za ${format(new Date(selectedMonth), "MMMM yyyy")}`}
      open={isOpen}
      onCancel={handleCancel}
      footer={[]}
      className="standardHeader"
      width={700}
    >
      <Flex vertical gap={14}>
        <div style={{ background: "#f8f8f5", borderRadius: 12, padding: 12 }}>
          <Text strong style={{ fontSize: 14, color: "#222" }}>
            Aktivne pozajmice
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            Pregled odbitaka i fleksibilnih uplata za izabrani mesec.
          </Text>
        </div>

        <List
          dataSource={activeLoans || []}
          locale={{ emptyText: "Nema aktivnih pozajmica za ovaj mesec" }}
          renderItem={(loan: any) => (
            <List.Item
              style={{
                border: "1px solid #f1f1f0",
                borderRadius: 16,
                marginBottom: 10,
                padding: "12px 14px",
                background: "#fff",
                boxShadow: "0 12px 30px rgba(0,0,0,0.05)",
                display: "block",
              }}
            >
              <Flex
                justify="space-between"
                align="flex-start"
                style={{ width: "100%" }}
                gap="small"
              >
                <div style={{ flex: 1 }}>
                  <Flex gap="small" align="center" wrap="wrap">
                    <Text strong style={{ fontSize: 15, color: "#111" }}>
                      {format(new Date(loan.startDate), "dd.MM.yyyy")}
                    </Text>
                    <span
                      style={{
                        padding: "2px 10px",
                        borderRadius: 999,
                        background:
                          loan.type === "flexible_payout"
                            ? "#f0f5ff"
                            : "#f6ffed",
                        color:
                          loan.type === "flexible_payout"
                            ? "#1d39c4"
                            : "#237804",
                        fontSize: 12,
                        border:
                          loan.type === "flexible_payout"
                            ? "1px solid #d6e4ff"
                            : "1px solid #d9f7be",
                      }}
                    >
                      {loan.type === "advance_salary"
                        ? "Plata unapred"
                        : loan.type === "installemnt_loan"
                          ? "Na rate"
                          : "Fleksibilno"}
                    </span>
                  </Flex>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>
                    {loan.type === "advance_salary"
                      ? "Jednokratno odbijanje"
                      : loan.type === "installemnt_loan"
                        ? `Rata ${
                            (getYear(new Date(selectedMonth)) -
                              getYear(new Date(loan.startDate))) *
                              12 +
                            (getMonth(new Date(selectedMonth)) -
                              getMonth(new Date(loan.startDate))) +
                            1
                          }/${loan.numberOfIntallments}`
                        : "Povlačenje uz uplate po unosu"}
                  </div>
                  {loan.notes && (
                    <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                      {loan.notes}
                    </div>
                  )}

                  {loan.type === "flexible_payout" && (
                    <div
                      style={{
                        marginTop: 10,
                        fontSize: 12,
                        color: "#3a3a3a",
                        background: "#f7f9fb",
                        border: "1px solid #eef0f2",
                        borderRadius: 10,
                        padding: "8px 10px",
                      }}
                    >
                      <Flex wrap="wrap" gap="small">
                        <span>
                          Ukupno vraćeno:{" "}
                          {(
                            loan.repaidAmount ??
                            loan.flexibleRepayments?.reduce(
                              (acc: number, rep: any) =>
                                acc + Number(rep.amount || 0),
                              0,
                            ) ??
                            0
                          ).toLocaleString("sr-RS")}{" "}
                          RSD
                        </span>
                        <span>
                          Preostalo:{" "}
                          {(
                            loan.outstandingAmount ??
                            calculateFlexibleOutstanding(loan)
                          ).toLocaleString("sr-RS")}{" "}
                          RSD
                        </span>
                        <span>
                          Ovaj mesec:{" "}
                          {calculateFlexibleRepaymentsForMonth(
                            loan,
                            selectedMonth,
                          ).toLocaleString("sr-RS")}{" "}
                          RSD
                        </span>
                      </Flex>
                      <div style={{ marginTop: 6 }}>
                        {loan.flexibleRepayments?.length ? (
                          <div>
                            <strong>Poslednje uplate:</strong>{" "}
                            {loan.flexibleRepayments
                              ?.slice()
                              .sort(
                                (a: any, b: any) =>
                                  new Date(b.date).getTime() -
                                  new Date(a.date).getTime(),
                              )
                              .slice(0, 3)
                              .map((repayment: any) => (
                                <div key={repayment._id || repayment.date}>
                                  {format(
                                    new Date(repayment.date),
                                    "dd.MM.yyyy",
                                  )}{" "}
                                  -{" "}
                                  {Number(repayment.amount || 0).toLocaleString(
                                    "sr-RS",
                                  )}{" "}
                                  RSD{" "}
                                  {repayment.description
                                    ? `(${repayment.description})`
                                    : ""}
                                </div>
                              ))}
                          </div>
                        ) : (
                          <span>Još nema evidentiranih uplata.</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ textAlign: "right", minWidth: 140 }}>
                  <Text style={{ color: "#111", fontWeight: 700 }}>
                    {(loan.type === "installemnt_loan"
                      ? loan.monthlyInstallment
                      : loan.type === "flexible_payout"
                        ? calculateFlexibleRepaymentsForMonth(
                            loan,
                            selectedMonth,
                          )
                        : loan.totalAmount
                    ).toLocaleString("sr-RS")}{" "}
                    RSD
                  </Text>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    Odbitak za mesec
                  </div>
                </div>
              </Flex>

              {loan.type === "flexible_payout" && (
                <div
                  style={{
                    marginTop: 10,
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px dashed #d9d9d9",
                    background: "#fafafa",
                  }}
                >
                  <Flex align="center" gap="small" wrap="wrap">
                    <div style={{ minWidth: 120 }}>
                      <Text strong style={{ fontSize: 12 }}>
                        Brza uplata
                      </Text>
                      <Text
                        type="secondary"
                        style={{ display: "block", fontSize: 10 }}
                      >
                        Veže se za izabrani mesec
                      </Text>
                    </div>
                    <Input
                      placeholder="Iznos"
                      size="small"
                      value={repaymentInputs[loan._id]?.amount}
                      onChange={(e) =>
                        handleRepaymentFieldChange(
                          loan._id,
                          "amount",
                          e.target.value,
                        )
                      }
                      style={{ width: 100 }}
                      type="number"
                    />
                    <DatePicker
                      size="small"
                      value={repaymentInputs[loan._id]?.date ?? selectedMonth}
                      onChange={(date) =>
                        handleRepaymentFieldChange(loan._id, "date", date)
                      }
                      placeholder="Datum"
                      style={{ minWidth: 130 }}
                    />
                    <Input
                      placeholder="Opis (opciono)"
                      size="small"
                      value={repaymentInputs[loan._id]?.description}
                      onChange={(e) =>
                        handleRepaymentFieldChange(
                          loan._id,
                          "description",
                          e.target.value,
                        )
                      }
                      style={{ flex: 1, minWidth: 150 }}
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => submitFlexibleRepayment(loan._id)}
                      loading={repaymentLoading[loan._id]}
                    >
                      Zabeleži
                    </Button>
                  </Flex>
                </div>
              )}

              <Flex
                justify="flex-end"
                style={{ marginTop: 10 }}
                align="center"
                gap="small"
              >
                <Popconfirm
                  title="Obrisati ovu pozajmicu?"
                  okText="Da"
                  cancelText="Ne"
                  onConfirm={async () => {
                    await axios.delete(
                      `${BASE_API_URL}/deleteLoan/${loan._id}/${modalData.userId._id}`,
                    );
                    onUpdate();
                  }}
                >
                  <Button danger size="small" type="text">
                    Obriši pozajmicu
                  </Button>
                </Popconfirm>
              </Flex>
            </List.Item>
          )}
        />

        {activeLoans?.length > 0 && (
          <Flex justify="end" style={{ marginTop: -2 }}>
            <Text strong style={{ fontSize: 13, color: "#444" }}>
              Ukupno za ovaj mesec:{" "}
              <span style={{ color: "#1890ff" }}>
                {totalLoans.toLocaleString("sr-RS")} RSD
              </span>
            </Text>
          </Flex>
        )}

        <div
          style={{
            border: "1px solid #f1f1f0",
            borderRadius: 16,
            padding: 14,
            background: "#fff",
            boxShadow: "0 12px 30px rgba(0,0,0,0.05)",
          }}
        >
          <Text strong style={{ fontSize: 14, color: "#222" }}>
            Dodaj novu pozajmicu
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            Unesi parametre i automatski ćemo je vezati za korisnika.
          </Text>

          {/* Forma za unos nove pozajmice */}
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            style={{ marginTop: 10 }}
          >
            <Form.Item
              name="type"
              label="Tip pozajmice"
              rules={[{ required: true, message: "Odaberite tip pozajmice" }]}
            >
              <Select placeholder="Odaberite tip">
                <Option value="advance_salary">Plata unapred</Option>
                <Option value="installemnt_loan">Pozajmica na rate</Option>
                <Option value="flexible_payout">
                  Fleksibilno (otplata po unosu)
                </Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="totalAmount"
              label="Ukupan iznos (RSD)"
              rules={[{ required: true, message: "Unesite iznos" }]}
            >
              <Input type="number" />
            </Form.Item>

            <Form.Item
              name="startDate"
              label="Datum početka"
              rules={[{ required: true, message: "Odaberite datum" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            {userWorkLocations.length > 0 && (
              <Form.Item
                name="locationId"
                label={t("Work Location")}
                initialValue={defaultLocationId}
                rules={[
                  {
                    required: true,
                    message: t("Please select work location!"),
                  },
                ]}
              >
                <Select placeholder={t("Select work location")}>
                  {userWorkLocations.map((location: any) => (
                    <Option key={location._id} value={location._id}>
                      {location.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.type !== curr.type}
            >
              {({ getFieldValue }) =>
                getFieldValue("type") === "installemnt_loan" && (
                  <Flex gap="small" wrap="wrap">
                    <Form.Item
                      name="numberOfIntallments"
                      label="Broj rata"
                      rules={[{ required: true, message: "Unesite broj rata" }]}
                      style={{ flex: 1, minWidth: 160 }}
                    >
                      <Input type="number" />
                    </Form.Item>
                    <Form.Item
                      name="monthlyInstallment"
                      label="Iznos rate (RSD)"
                      rules={[
                        { required: true, message: "Unesite iznos rate" },
                      ]}
                      style={{ flex: 1, minWidth: 160 }}
                    >
                      <Input type="number" />
                    </Form.Item>
                  </Flex>
                )
              }
            </Form.Item>

            <Form.Item name="notes" label="Napomena">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Button type="primary" htmlType="submit" block>
              Sačuvaj pozajmicu
            </Button>
          </Form>
        </div>
      </Flex>
    </Modal>
  );
};

export default LoanPaymentModal;
